// GENERIC shell — the AUTO-renew email (A21). Reassuring: no pricing, no
// comparison grid, no payment card. Logic ported verbatim from
// rensure-sme-smp/lib/messaging/layout.ts (genericEmailHtml); markup in the HQ welcome-email design.

import type { BrandConfig } from "../brands";
import { COLLECTION_LABEL, COLLECTION_SCHEDULE, type CollectionRow } from "../collection";
import type { PaymentType } from "../types";
import { escapeHtml } from "../format";
import { type BrandedEmailArgs, type Fact, art, bodyRow, emailDocument, notRenewingHtml, policyCardHtml, row, table } from "./chrome";

export type GenericEmailArgs = BrandedEmailArgs & {
  /** Payment-collection schedule shown subtly in the auto email. */
  collectionMethod?: string;
  collectionRows?: CollectionRow[];
  /** Overrides the strap line inside the "No action needed" highlight. */
  noActionText?: string;
  /** Label → value card shown INSTEAD of the collection schedule (auto DD). */
  detail?: { title: string; rows: [string, string][] };
  /** Sentence tail of the "Not planning to renew?" block. */
  notRenewingTail?: string;
};

/** Bordered card of label → value rows, welcome-email fact-table style. */
const factCard = (brand: BrandConfig, title: string, rows: [string, string][]): string =>
  `<p style="margin:22px 0 10px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;text-transform:uppercase;color:${brand.primary};">${title}</p>
              ${table(
                rows
                  .map(
                    ([label, val]) =>
                      `<tr><td width="42%" valign="top" style="padding:11px 12px 11px 0;border-top:1px solid ${brand.line};font-size:13px;line-height:20px;color:${brand.text};">${label}</td><td valign="top" style="padding:11px 0;border-top:1px solid ${brand.line};font-size:14px;line-height:20px;font-weight:700;color:${brand.ink};">${val}</td></tr>`,
                  )
                  .join(""),
                `border-bottom:1px solid ${brand.line};`,
              )}`;

export function genericEmailHtml(args: GenericEmailArgs): string {
  const { brand, data, bodyHtml, callNumber } = args;
  const method = args.detail ? "Monthly Direct Debit" : args.collectionMethod;
  const facts: Fact[] = method ? [["policy", "Payment", escapeHtml(method)]] : [];

  // A calm, accent-tinted highlight reinforcing the "nothing to do" message.
  const noAction = table(
    `<tr><td width="56" valign="top" style="padding:16px 0 16px 18px;">${art(brand, "certificate.png", 42)}</td><td valign="top" style="padding:16px 18px 16px 14px;">
                    <span style="display:block;font-size:11px;line-height:16px;font-weight:700;letter-spacing:1.6px;text-transform:uppercase;color:${brand.primary};margin:0 0 4px;">No action needed</span>
                    <span style="font-size:14px;line-height:22px;color:${brand.ink};">${args.noActionText ?? "Your renewal is all set — your cover will continue automatically and your documents are attached."}</span>
                  </td></tr>`,
    `margin:8px 0 4px;background:${brand.soft};border:1px solid ${brand.line};border-radius:14px;`,
  );

  // The card under the copy: either the collection schedule (auto
  // policies are collected, not chased) or the Direct Debit summary.
  const rows = args.collectionRows ?? [];
  const collectionBlock = args.detail
    ? factCard(
        brand,
        escapeHtml(args.detail.title),
        args.detail.rows.map(([label, val]) => [escapeHtml(label), escapeHtml(val)]),
      )
    : rows.length
      ? factCard(
          brand,
          `When we'll collect your payment${args.collectionMethod ? ` · ${escapeHtml(args.collectionMethod)}` : ""}`,
          rows.map((r) => [
            r.day === 0 ? "Renewal day (AM)" : `${r.day} day${r.day === 1 ? "" : "s"} before`,
            escapeHtml(r.label),
          ]),
        )
      : "";

  return emailDocument({
    brand,
    eyebrow: "Automatic renewal",
    heading: "You're all set for renewal",
    policyCard: policyCardHtml(brand, data, facts),
    marketing: false,
    iptNote: false,
    rows: `${bodyRow(brand, `${bodyHtml}\n${noAction}\n${collectionBlock}`, "padding-bottom:24px;")}
${row(notRenewingHtml(brand, callNumber, args.notRenewingTail), "padding:0 36px 30px;", "pad")}`,
  });
}

/** The collection schedule + label for a payment type (defaults to In Full). */
export const collectionFor = (paymentType: PaymentType = "CASH") => ({
  collectionMethod: COLLECTION_LABEL[paymentType],
  collectionRows: COLLECTION_SCHEDULE[paymentType],
});
